<!-- ActivityCVView.vue -->
<template>
  <section class="cv">
    <header class="header">
      <div class="row space-between">
        <h2 class="title">{{ title }}</h2>
        <span v-if="cv.level" class="tag">Level: {{ cv.level }}</span>
      </div>
    </header>

    <div v-if="parseError" class="error">
      {{ parseError }}
    </div>

    <div v-else>
      <!-- Story -->
      <section v-if="cv.story" class="block">
        <h3>Summary</h3>
        <p class="story">{{ cv.story }}</p>
      </section>

      <!-- Achievements -->
      <section class="block">
        <h3>Achievements</h3>
        <ul v-if="achievementsSorted.length" class="cards">
          <li v-for="(a, i) in achievementsSorted" :key="i" class="card">
            <div class="row space-between">
              <span class="badge">{{ a.type || '—' }}</span>
              <span class="muted">{{ dateRange(a.start_date, a.end_date) }}</span>
            </div>

            <div class="row wrap">
              <strong class="role">{{ a.role || '—' }}</strong>
              <span v-if="a.level" class="tag">{{ a.level }}</span>
            </div>

            <div v-if="a.place" class="row place">📍 {{ a.place }}</div>

            <p v-if="a.story" class="desc">{{ a.story }}</p>

            <div v-if="Array.isArray(a.functions) && a.functions.length" class="chips">
              <span v-for="(f, k) in a.functions" :key="k" class="chip">{{ f }}</span>
            </div>
          </li>
        </ul>
        <p v-else class="empty">No achievements.</p>
      </section>

      <!-- Certificates -->
      <section class="block">
        <h3>Certificates</h3>
        <ul v-if="cv.certificates.length" class="cards">
          <li v-for="(c, i) in cv.certificates" :key="i" class="card">
            <div class="row space-between">
              <strong>{{ c.certificate_type || '—' }}</strong>
              <span class="muted">{{ dateRange(c.issue_date, c.expiration_date) }}</span>
            </div>
            <p v-if="c.description" class="desc">{{ c.description }}</p>
          </li>
        </ul>
        <p v-else class="empty">No certificates.</p>
      </section>

      <!-- Skills -->
      <section class="block">
        <h3>Skills</h3>
        <ul v-if="cv.skills.length" class="skills">
          <li v-for="(s, i) in cv.skills" :key="i" class="skill">
            <span class="skill-name">{{ s.skill_type || s.other_skill_type || '—' }}</span>
            <span v-if="s.level" class="muted"> · {{ s.level }}</span>
          </li>
        </ul>
        <p v-else class="empty">No skills.</p>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  /**
   * Raw JSON string with shape:
   * { story, level, achievements:[], certificates:[], skills:[] }
   */
  jsonText: { type: String, required: true },
  /** Optional heading shown at the top */
  title: { type: String, default: 'Activity CV' },
  /** Locale used for date rendering */
  locale: { type: String, default: 'en-GB' }
})

const parseError = ref('')

/** Safely parse and normalize the CV object */
const cv = computed(() => {
  try {
    parseError.value = ''
    const data = JSON.parse(props.jsonText)

    return {
      story: data?.story ?? '',
      level: data?.level ?? '',
      achievements: Array.isArray(data?.achievements) ? data.achievements : [],
      certificates: Array.isArray(data?.certificates) ? data.certificates : [],
      skills: Array.isArray(data?.skills) ? data.skills : []
    }
  } catch {
    parseError.value = 'Invalid JSON: could not parse.'
    return { story: '', level: '', achievements: [], certificates: [], skills: [] }
  }
})

/** Sort achievements by start date (newest first), NaN dates go to the end */
const achievementsSorted = computed(() => {
  const items = [...cv.value.achievements]
  items.sort((a, b) => {
    const ad = Date.parse(a?.start_date || a?.end_date || '')
    const bd = Date.parse(b?.start_date || b?.end_date || '')
    const aBad = Number.isNaN(ad)
    const bBad = Number.isNaN(bd)
    if (aBad && bBad) return 0
    if (aBad) return 1
    if (bBad) return -1
    return bd - ad // descending
  })
  return items
})

function fmtDate(iso) {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  return new Date(t).toLocaleDateString(props.locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  })
}

function dateRange(start, end) {
  const s = fmtDate(start)
  const e = fmtDate(end)
  if (s && e) return `${s} – ${e}`
  return s || e || ''
}
</script>

<style scoped>
/* Layout */
.cv { max-width: 860px; margin: 0 auto; padding: 1.25rem; background: #fff; }
.header { margin-bottom: .75rem; }
.title { font-size: 1.5rem; margin: 0; }
.block { margin-top: 1.25rem; }
.block > h3 {
  font-size: .95rem;
  font-weight: 700;
  letter-spacing: .03em;
  text-transform: uppercase;
  color: #4b5563;
  margin: 0 0 .5rem;
}

/* Lists / cards */
.cards { list-style: none; padding: 0; margin: 0; display: grid; gap: .75rem; }
.card {
  border: 1px solid #e5e7eb;
  border-radius: .75rem;
  padding: .9rem 1rem;
  background: #ffffff;
}
.skills { list-style: none; padding: 0; margin: 0; display: grid; gap: .35rem; }
.skill { padding: .25rem 0; }

/* Text, badges */
.story { line-height: 1.6; }
.badge { background: #eef2ff; color: #3730a3; padding: .15rem .5rem; border-radius: .5rem; font-size: .8rem; }
.tag { background: #f1f5f9; padding: .1rem .5rem; border-radius: .5rem; margin-left: .5rem; font-size: .8rem; color: #334155; }
.muted { color: #6b7280; font-size: .85rem; }
.empty { color: #9ca3af; font-style: italic; }

/* Chips */
.chips { margin-top: .3rem; display: flex; flex-wrap: wrap; gap: .25rem; }
.chip { border: 1px solid #e5e7eb; border-radius: 999px; padding: .1rem .5rem; font-size: .75rem; color: #334155; }

/* Utility rows */
.row { display: flex; align-items: center; gap: .5rem; }
.wrap { flex-wrap: wrap; }
.space-between { justify-content: space-between; }
.place { color: #374151; font-size: .9rem; }
.desc { margin: .4rem 0 0; line-height: 1.5; }

/* Error */
.error { color: #b91c1c; background: #fee2e2; border: 1px solid #fecaca; padding: .5rem .75rem; border-radius: .5rem; }
</style>
